import dynamic from 'next/dynamic';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import KrpanoViewer from '@components/Krpano/KrpanoViewer';
import { Type_RefFuncKrpano } from '@components/Krpano/types';
import {
  selectConfigNadir,
  selectConfigSoundProject,
  selectListScene,
  selectPrevSceneSelected,
  selectSceneSelected,
  selectThemeConfigGlobal,
} from '@containers/Home/selectors';
import { useKrpano } from '@containers/Home/hook/useKrpano';
import {
  useTourEventState,
  useCollaborationMode,
} from '@containers/Home/global/TourStats';
import BeforeAfterSlider from '@components/BeforeAfterSlider';
import { TourWrapper } from './styled';
import { I_Scene } from '../types';
import { ID_KRPANO_CONTAINER } from '../constants';

const DialogPost = dynamic(() => import('@components/DialogPost'));
const Map = dynamic(() => import('@containers/Home/components/Map'));
const TourSound = dynamic(
  () => import('@containers/Home/components/Tour.Sound'),
);
const TourEvent = dynamic(
  () => import('@containers/Home/components/Tour.Event'),
);
const Floorplan = dynamic(
  () => import('@containers/Home/components/Floorplan'),
);
const MultipleStagingThumb = dynamic(
  () => import('@containers/Home/components/MultipleStagingThumb'),
);

export interface I_Tour {
  onNextScene: () => void;
  children?: React.ReactNode;
  onChangeScene?: (scene: I_Scene) => void;
}

const Tour = ({ onChangeScene, onNextScene, children }: I_Tour) => {
  const listScene = useSelector(selectListScene);
  const nadirConfig = useSelector(selectConfigNadir);
  const themeConfig = useSelector(selectThemeConfigGlobal);
  const sceneSelected = useSelector(selectSceneSelected);
  const configSound = useSelector(selectConfigSoundProject);
  const prevSceneSelected = useSelector(selectPrevSceneSelected);

  const krpanoRef = useRef<Type_RefFuncKrpano>(null!);
  const [krpanoRefState, setKrpanoRefState] = useState<Type_RefFuncKrpano>(
    null!,
  );

  const urlTour = useMemo(
    () =>
      sceneSelected?.media?.src_preview ||
      sceneSelected?.media?.src_thumb ||
      sceneSelected?.media?.src,
    [sceneSelected],
  );

  const isShowMultipleStagingThumb = useMemo(
    () =>
      sceneSelected?.mode === 'staging' &&
      sceneSelected?.staging.length &&
      sceneSelected?.staging.filter(item => item.media).length &&
      !sceneSelected?.configStaging?.modeDisplay,
    [sceneSelected],
  );

  const sceneChildsMultipleStaging = useMemo(
    () =>
      (
        (sceneSelected?.mode === 'staging' &&
        !!sceneSelected?.configStaging?.modeDisplay
          ? sceneSelected?.staging
          : []) || []
      )
        .filter(item => item.media)
        .map(item => ({
          ...item,
          urlTour:
            item?.media?.src_preview ||
            item?.media?.src_thumb ||
            item?.media?.src,
          config: sceneSelected?.config,
          configPlugin: sceneSelected?.configPlugin,
        })),
    [sceneSelected],
  );

  const [tourEventStats, setTourEventState] = useTourEventState('state');
  const [, setKrpanoInstance] = useCollaborationMode('krpanoRef');

  const {
    isShowIconMap,
    isShowIconFloorplan,
    onCloseDialogPostProject,
    //
    onStartRotate,
    onClickHotspot,
    onStopHotspotSound,
    onStartHotspotSound,
    onChangeAutoChangeScene,
    onRemoveAllChevronHotspot,
    onToggleModeMultipleStaging,
  } = useKrpano({ krpanoRef: krpanoRefState });

  /**
   * NOTE: destroy sound prevScene when change scene
   */
  useEffect(() => {
    if (prevSceneSelected) {
      onStopHotspotSound();
      onRemoveAllChevronHotspot(prevSceneSelected.hotspots);
      onToggleModeMultipleStaging(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevSceneSelected]);

  /**
   * NOTE: auto play sound when change scene (only when allow sound autoplay)
   */
  useEffect(() => {
    if (configSound.on && sceneSelected) onStartHotspotSound();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneSelected, configSound]);

  return (
    <TourWrapper>
      <BeforeAfterSlider
        mode={sceneSelected?.configStaging?.modeDisplay}
        scenes={
          tourEventStats?.isActiveModeMultipleStaging
            ? sceneChildsMultipleStaging
            : []
        }
        nadirConfig={nadirConfig || undefined}
        firstPane={
          <KrpanoViewer
            ref={node => {
              if (!krpanoRef.current) {
                krpanoRef.current = node;
                setKrpanoRefState(krpanoRef.current);
              }
            }}
            nadirConfig={nadirConfig || undefined}
            isLoaded={tourEventStats?.isLoaded}
            listScene={listScene}
            urlTour={urlTour || ''}
            setIsLoaded={isLoaded => {
              setTourEventState(prev => ({ ...prev, isLoaded }));
              if (isLoaded) setKrpanoInstance(krpanoRefState);
              else if (!isLoaded) setKrpanoInstance(null);
            }}
            krpanoId={ID_KRPANO_CONTAINER}
            onClickHotspot={onClickHotspot}
            divId="krpanoContainerSnapshot"
            scene={sceneSelected || undefined}
            styleContainer={{
              height: '100%',
              width:
                sceneSelected?.configStaging?.modeDisplay === 'split'
                  ? '100%'
                  : '100vw',
            }}
            onChangeAutoChangeScene={onChangeAutoChangeScene}
            isTurnOffFollowMode={!!sceneChildsMultipleStaging.length}
          />
        }
      />

      {krpanoRefState && (
        <>
          {React.Children.map(children, child => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                krpanoRef: krpanoRefState,
              });
            }
            return child;
          })}
          <TourSound krpanoRef={krpanoRefState} />
          <TourEvent
            krpanoRef={krpanoRefState}
            onNextScene={onNextScene}
            onStartRotate={onStartRotate}
            isLoaded={tourEventStats?.isLoaded}
          />
        </>
      )}

      {tourEventStats?.idPostProjectSelected && (
        <DialogPost
          onCancel={onCloseDialogPostProject}
          isUseDefaultStyle={themeConfig?.selected === 'default'}
          idPost={tourEventStats?.idPostProjectSelected}
          visible={tourEventStats?.visibleDialogPostProject}
        />
      )}

      {isShowIconMap && <Map onChangeScene={onChangeScene} />}
      {isShowIconFloorplan && <Floorplan onChangeScene={onChangeScene} />}

      {isShowMultipleStagingThumb && (
        <MultipleStagingThumb
          scenes={
            sceneSelected?.staging?.map(item => ({
              ...item,
              config: sceneSelected?.config,
            })) || []
          }
          callKrpano={krpanoRef.current?.call}
        />
      )}
    </TourWrapper>
  );
};

export default memo(Tour);
